import { clearAuthCache, getAccessToken } from '@/lib/auth';
import { getEffectiveAuthProvider } from '@/lib/auth/effectiveAuthProvider';
import { useAuthSessionMetadata } from '@/lib/auth/hooks/useAuthSessionMetadata';
import { isTauri } from '@/tauri/utils';
import { AuthProvider } from '@/utils/auth/persistedAuthProvider';
import { useAuthState } from '@heidi/auth/state';
import { useCallback } from 'react';

// The cache surfaces auth-state through atoms, not through a thrown / returned
// error. The shim's `error` field stays as a stable `null` for backwards
// compatibility — module-scope const so consumers can rely on a referentially
// stable value without a wasted useMemo per render.
const SHIM_ERROR_FIELD = null;

/**
 * Backwards-compatible shim around the auth token cache. Returns the
 * same 9 top-level fields (`accessToken`, `user`, `userRefId`,
 * `kindeUserId`, `isAuthenticated`, `isLoading`, `error`, `getToken`,
 * `refreshSession`) that consumers across the codebase destructure
 * today. The underlying data flows through the cache — no more
 * TanStack Query `/api/auth0-session` fetch from inside this hook;
 * the cache's scheduler drives refresh, the cache populates
 * `authSessionMetadataAtom`, this hook reads from there.
 *
 * Consumers should migrate to `useAuthSessionMetadata()` directly
 * (and `useIsAuthenticated()` for the boolean) — the shim exists so
 * that migration can land incrementally without breaking the 10+
 * existing destructuring sites in one go.
 */

// Query-key + invalidate helpers are no-op shims retained so existing
// test imports (and the rare external invalidation site) keep working
// during the migration. Once the shim has no remaining importers, both
// can be deleted.
export const GET_AUTH0_SESSION = 'getAuth0Session';

export const invalidateAuth0Session = (): Promise<void> => {
  // Cache invalidation now flows through `clearAuthCache`; preserved
  // as a Promise-returning shape for backwards compatibility with the
  // old TanStack Query async signature.
  clearAuthCache();
  return Promise.resolve();
};

export interface Auth0User {
  sub: string;
  email?: string;
  name?: string;
  picture?: string;
  [key: string]: unknown;
}

export interface Auth0SessionState {
  /** Auth0 access token */
  accessToken: string | null;
  /** Auth0 user object */
  user: Auth0User | null;
  /**
   * Heidi user_ref_id parsed from the access token's `user_properties` claim.
   * Only populated for Auth0 web users; null otherwise (Kinde/FHIR/Desktop/non-authenticated).
   */
  userRefId: string | null;
  /**
   * Kinde user id parsed from the access token's `user_properties` claim
   * (synced into the Auth0 token for cross-provider compatibility).
   * Only populated for Auth0 web users; null otherwise (Kinde/FHIR/Desktop/non-authenticated).
   */
  kindeUserId: string | null;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Whether the session is being loaded */
  isLoading: boolean;
  /** Any error that occurred during session fetch */
  error: Error | null;
  /** Function to get the current token */
  getToken: () => string | null;
  /**
   * Refresh the session; resolves to the current Auth0 access token
   * (or `null` when there is nothing to refresh — steady-state Kinde
   * web short-circuits, and the cache returns null on bare-null /
   * transient outcomes).
   *
   * By default this is a **non-forced** read: the cache serves a
   * still-valid token from its slot if there is one and only calls
   * the provider source-of-truth when the slot is missing/expired.
   * This is the correct default for keep-warm/poll callers.
   *
   * Pass `{ force: true }` when the caller genuinely needs a fresh
   * grant — e.g. the migration-completion "adopt Auth0 first" step,
   * where writing the current cached token into Redux would leave
   * the pre-migration state in place. Forced calls route through
   * `getAccessToken({ forceRefresh: true })`, which hits the
   * `?refresh=true` endpoint.
   */
  refreshSession: (options?: { force?: boolean }) => Promise<string | null>;
}

export const useAuth0Session = (): Auth0SessionState => {
  const session = useAuthSessionMetadata();
  const state = useAuthState();

  // Self-gate: only Auth0-web users see live cache state. Provider
  // resolution goes through `getEffectiveAuthProvider()` so the
  // Kinde→Auth0 migration window (cookie=AUTH0, Redux=KINDE) starts
  // returning Auth0 data the moment the cookie flips — gating on
  // Redux would create a chicken-and-egg with Redux waiting for this
  // shim to pull the Auth0 session. Every other caller (Kinde web,
  // Keycloak web, anything in Tauri, FHIR, pre-cookie unknown)
  // preserves the legacy "query disabled" shape: isLoading=false,
  // isAuthenticated=false, null metadata.
  const isAuth0Web = !isTauri() && getEffectiveAuthProvider() === AuthProvider.AUTH0;

  const accessToken = isAuth0Web ? (session?.accessToken ?? null) : null;
  const user = isAuth0Web
    ? ((session?.user as Auth0User | null | undefined) ?? null)
    : null;
  const userRefId = isAuth0Web ? (session?.userRefId ?? null) : null;
  const kindeUserId = isAuth0Web ? (session?.kindeUserId ?? null) : null;
  const isAuthenticated = isAuth0Web && state === 'authenticated';
  const isLoading = isAuth0Web && state === 'loading';

  const getToken = useCallback(() => accessToken, [accessToken]);

  const refreshSession = useCallback(
    async (options?: { force?: boolean }): Promise<string | null> => {
      // Read the cookie fresh at call time, NOT from `isAuth0Web` captured
      // at render. The Kinde→Auth0 migration flow polls the cookie and
      // calls this between renders — the closure could hold a pre-flip
      // value while the cookie has already flipped to AUTH0; reading
      // fresh ensures we still fire in that window.
      const effective = getEffectiveAuthProvider();

      // Steady-state Kinde-web users (cookie=KINDE, !isTauri) short-circuit:
      // firing the refresh would dispatch to readKindeWeb (still a
      // stub returning null until the /api/kinde-session sibling ships),
      // which the cache classifies as bare-null and flips authStateAtom
      // to 'unauthenticated'. useIsAuthenticated is shielded by its own
      // Kinde-web fallback, but direct authStateAtom / session-metadata
      // consumers aren't — so we never write that bare-null in the first
      // place. The migration window (cookie=AUTH0) does NOT short-circuit:
      // effective === AUTH0 there, so the call fires and the cache routes
      // through readAuth0Web as intended.
      if (!isTauri() && effective === AuthProvider.KINDE) return null;

      // Default is a non-forced read — the migration modal's 2s
      // fallback poll called this on every tick, and an unconditional
      // `forceRefresh: true` produced a fresh refresh-token grant per
      // tick during a server hiccup. Keep-warm callers get the cache
      // slot when it's still valid; only `{ force: true }` callers
      // hit `?refresh=true` and force a rotation.
      const forceRefresh = options?.force === true;
      return getAccessToken({ forceRefresh });
    },
    []
  );

  return {
    accessToken,
    user,
    userRefId,
    kindeUserId,
    isAuthenticated,
    isLoading,
    error: SHIM_ERROR_FIELD,
    getToken,
    refreshSession,
  };
};

export default useAuth0Session;
